import type {DealCard, DealExchange, DealJournalEntry, DealRow, DealStatus} from 'compob2b-libs';
import {formatDay, orderModelTitle, orderPrice} from '../reservation-orders/reservation-order.util';
import type {JournalRow} from '../shared/ui/journal.component';
import type {StatusTone} from '../shared/ui/status-appearance.util';

/** Алиас крошки карточки сделки: маршрут — `cabinet.routes.ts`, подпись «Сделка №N» ставит карточка. */
export const DEAL_CARD_CRUMB = 'deal';

/** Статусы сделки (Э9-1, допущение до ответа 1С-9). */
export const DEAL_STATUS_LABELS: Record<DealStatus, string> = {
  PROCESSING: $localize`:@@DealStatusProcessing:Обработка`,
  SENT: $localize`:@@DealStatusSent:Отправка`,
  CONFIRMED: $localize`:@@DealStatusConfirmed:Подтверждена`,
  CANCELLED: $localize`:@@DealStatusCancelled:Отменена`,
};

/** Тона по правилу `badge()` прототипа (зелёный — успех, красный — отмена, прочее синий/жёлтый). */
export const DEAL_STATUS_TONES: Record<DealStatus, StatusTone> = {
  PROCESSING: 'blue',
  SENT: 'amber',
  CONFIRMED: 'green',
  CANCELLED: 'red',
};

export const DEAL_STATUSES: DealStatus[] = ['PROCESSING', 'SENT', 'CONFIRMED', 'CANCELLED'];

/** Статус обмена с 1С (Э9-2). */
export const DEAL_EXCHANGE_LABELS: Record<DealExchange, string> = {
  PENDING: $localize`:@@DealExchangePending:Ожидает передачи`,
  SENT: $localize`:@@DealExchangeSent:Передано`,
  ERROR: $localize`:@@DealExchangeError:Ошибка передачи`,
};

export const DEAL_EXCHANGE_TONES: Record<DealExchange, StatusTone> = {
  PENDING: 'gray',
  SENT: 'green',
  ERROR: 'red',
};

/** Подпись статуса, устойчивая к коду, которого портал не знает. */
export function dealStatusLabel(status: string | null | undefined): string {
  return (status && DEAL_STATUS_LABELS[status as DealStatus]) || status || '—';
}

export function dealStatusTone(status: string | null | undefined): StatusTone {
  return (status && DEAL_STATUS_TONES[status as DealStatus]) || 'blue';
}

export function dealExchangeLabel(exchange: string | null | undefined): string {
  return (exchange && DEAL_EXCHANGE_LABELS[exchange as DealExchange]) || exchange || '—';
}

/** «Передано · №ERP-1», без номера — «Передано»; прочие состояния — только подпись. */
export function dealExchangeText(row: Pick<DealRow, 'exchange' | 'erpDocumentNumber'>): string {
  const label = dealExchangeLabel(row.exchange);
  return row.exchange === 'SENT' && row.erpDocumentNumber ? `${label} · №${row.erpDocumentNumber}` : label;
}

/** Сырой статус 1С показываем, только когда он что-то добавляет к портальному. */
export function dealErpStatusShown(card: Pick<DealCard, 'status' | 'erpStatus'>): string | null {
  const erp = card.erpStatus?.trim();
  return erp && erp !== card.status ? erp : null;
}

/** Склад отгрузки: название, иначе uid из 1С; нет склада — `null`. */
export function dealWarehouseText(card: Pick<DealCard, 'warehouse'>): string | null {
  return card.warehouse ? (card.warehouse.header || card.warehouse.uid) : null;
}

/** Правка открыта, пока сделка в «Обработке» или «Отправке» (РМ 24, Э9-3). */
export function isDealOpen(status: DealStatus | string): boolean {
  return status === 'PROCESSING' || status === 'SENT';
}

/** Строка CSV — значения колонок списка в порядке таблицы. */
export function dealCsvRow(row: DealRow): string[] {
  return [
    `№${row.number} ${formatDay(row.createdAt)}`,
    `№${row.reservationOrder.number}`,
    `${row.client.company} ${row.client.inn}`,
    row.owner.header,
    orderModelTitle(row.product),
    dealStatusLabel(row.status),
    dealExchangeText(row),
  ];
}

export const DEAL_LOG_ACTION_LABELS: Record<string, string> = {
  CREATED: $localize`:@@DealLogCreated:Сделка создана`,
  SENT: $localize`:@@DealLogSent:Передана в 1С`,
  SEND_FAILED: $localize`:@@DealLogSendFailed:Ошибка передачи в 1С`,
  RESENT: $localize`:@@DealLogResent:Повторная отправка`,
  STATUS_CHANGED: $localize`:@@DealLogStatusChanged:Статус изменён`,
  CANCELLED: $localize`:@@DealLogCancelled:Сделка отменена`,
  COMMENTED: $localize`:@@DealLogCommented:Описание изменено`,
  ATTACHMENT_ADDED: $localize`:@@OrderLogFileAdded:Файл добавлен`,
  ATTACHMENT_REMOVED: $localize`:@@OrderLogFileRemoved:Файл удалён`,
};

/** Поля снимка, которые показываем в журнале, в этом порядке; служебные id не показываем. */
const DEAL_LOG_FIELDS: Record<string, string> = {
  status: $localize`:@@Status:Статус`,
  erpStatus: $localize`:@@DealErpStatus:Статус в 1С`,
  exchange: $localize`:@@DealExchange:Статус обмена`,
  exchangeError: $localize`:@@DealExchangeErrorText:Ошибка обмена`,
  erpDocumentNumber: $localize`:@@DealErpDocument:Документ 1С`,
  closeReason: $localize`:@@DealCloseReason:Причина отмены`,
  client: $localize`:@@OrderEndClient:Конечный клиент`,
  counterParty: $localize`:@@OrderColumnDealer:Дилер`,
  author: $localize`:@@OrderManager:Менеджер`,
  product: $localize`:@@ModelDetailModel:Модель`,
  price: $localize`:@@OrderColumnPrice:Стоимость`,
  contractNumber: $localize`:@@DealContractNumber:Номер договора`,
  vin: 'VIN',
  warehouse: $localize`:@@DealWarehouse:Склад отгрузки`,
  comment: $localize`:@@DealDescription:Описание`,
  fileName: $localize`:@@OrderLogFile:Файл`,
};

const CLOSE_REASON_LABELS: Record<string, string> = {
  MANUAL: $localize`:@@DealCloseManual:Отменена в портале`,
  ERP: $localize`:@@DealCloseErp:Отменена в 1С`,
};

function logValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  switch (field) {
    case 'status':
      return dealStatusLabel(String(value));
    case 'exchange':
      return dealExchangeLabel(String(value));
    case 'closeReason':
      return CLOSE_REASON_LABELS[String(value)] ?? String(value);
    case 'price':
      return orderPrice(Number(value)) ?? '—';
    default:
      return String(value);
  }
}

/**
 * Запись журнала → строка кирпича `journal`. Бэк кладёт в снимки только изменившиеся поля:
 * создание — «Поле: значение», изменение — «Поле: было → стало». Автор `SYSTEM` — «Система».
 */
export function toDealJournalRow(entry: DealJournalEntry): JournalRow {
  const before = entry.before ?? {};
  const after = entry.after ?? {};
  const changes = Object.keys(DEAL_LOG_FIELDS)
    .filter(field => field in before || field in after)
    .filter(field => !entry.before || !entry.after || before[field] !== after[field])
    .map(field => {
      const label = DEAL_LOG_FIELDS[field];
      if (!entry.before || !(field in before)) {
        return `${label}: ${logValue(field, after[field])}`;
      }
      if (!entry.after || !(field in after)) {
        return `${label}: ${logValue(field, before[field])}`;
      }
      return `${label}: ${logValue(field, before[field])} → ${logValue(field, after[field])}`;
    });
  const author = entry.actorName === 'SYSTEM' ? $localize`:@@BindingLogSystem:Система` : entry.actorName;
  return {at: entry.createdAt, author, action: DEAL_LOG_ACTION_LABELS[entry.action] ?? entry.action, changes};
}
